import { useState, useEffect, useCallback } from 'react';
import { fetchProductById } from '../controllers/product-controller';
import { CartService } from '../../../../services/cartService';
import { isStockRequired, UNLIMITED_ORDER_QUANTITY } from '../../../../utils/stock';

export const useProductViewModel = (productId, depotId = null) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(null);
  const [cartMessage, setCartMessage] = useState({ show: false, type: '', message: '' });
  
  // Group variants by their first variant value (e.g., color)
  const [variantGroups, setVariantGroups] = useState({});
  
  // Extract unique variant types (color, size, etc.)
  const [variantTypes, setVariantTypes] = useState([]);
  
  // Track selected variant values
  const [selectedVariantValues, setSelectedVariantValues] = useState({});
  
  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await fetchProductById(productId, depotId);
      setProduct(data);
      
      // Process variants
      if (data.productVariants && data.productVariants.length > 0) {
        // Set first in-stock variant as default, or first variant if none in stock
        const inStockVariant = data.productVariants.find(v => v.stock > 0);
        const defaultVariant = inStockVariant || data.productVariants[0];
        setSelectedVariant(defaultVariant);
        
        // Set active image
        if (defaultVariant.imageUrl && defaultVariant.imageUrl.length > 0) {
          setActiveImage(defaultVariant.imageUrl[0]);
        } else if (data.imageUrl && data.imageUrl.length > 0) {
          setActiveImage(data.imageUrl[0]);
        }
        
        // Group variants
        processVariants(data.productVariants);
      } else {
        // Handle non-variant products
        setActiveImage(data.imageUrl && data.imageUrl.length > 0 ? data.imageUrl[0] : null);
      }
    } catch (err) {
      setError('Failed to load product. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [productId, depotId]);
  
  const processVariants = (variants) => {
    // Extract variant types from the first variant (assuming all variants have the same structure)
    if (variants.length > 0 && variants[0].variantValue) {
      // Create groups based on first variant value (typically color)
      const groups = {};
      variants.forEach(variant => {
        if (variant.variantValue && variant.variantValue.length > 0) {
          // Extract variant values
          const values = variant.variantValue.map(v => v.variantValueId);
          
          // Use the first value as the group key (typically color)
          const groupKey = values[0];
          if (!groups[groupKey]) {
            groups[groupKey] = [];
          }
          groups[groupKey].push(variant);
        }
      });
      setVariantGroups(groups);
      
      // Extract and set default selected values from first variant
      const firstVariant = variants[0];
      if (firstVariant.variantValue && firstVariant.variantValue.length > 0) {
        const defaultValues = {};
        firstVariant.variantValue.forEach((vv, index) => {
          defaultValues[`type${index}`] = vv.variantValueId;
        });
        setSelectedVariantValues(defaultValues);
        
        // Set variant types (color, size, etc.) - This is an approximation since API doesn't provide type names
        setVariantTypes(['Color', 'Size']);
      }
    }
  };
  
  const handleVariantChange = (variantId) => {
    const variant = product.productVariants.find(v => v.id === variantId);
    if (variant) {
      setSelectedVariant(variant);
      
      // Update active image if the variant has images
      if (variant.imageUrl && variant.imageUrl.length > 0) {
        setActiveImage(variant.imageUrl[0]);
      }
      
      // Reset quantity to 1 when changing variants
      setQuantity(1);
    }
  };
  
  const handleVariantValueChange = (typeIndex, valueId) => {
    // Update selected values
    const newSelectedValues = {
      ...selectedVariantValues,
      [`type${typeIndex}`]: valueId
    };
    setSelectedVariantValues(newSelectedValues);
    
    // Find matching variant - IMPORTANT FIX HERE
    const matchingVariant = findMatchingVariant(newSelectedValues);
    
    if (matchingVariant) {
      // If we found a valid matching variant, use it
      handleVariantChange(matchingVariant.id);
    } else {
      // If no valid matching variant exists, find the best alternative
      // This is crucial to avoid auto-switching to other combinations
      findAlternativeVariant(newSelectedValues, typeIndex);
    }
  };

  const findAlternativeVariant = (selectedValues, changedTypeIndex) => {
    if (!product || !product.productVariants) return;
    
    // 1. First, try to preserve the just-changed selection by finding variants that match it
    const justChangedValue = selectedValues[`type${changedTypeIndex}`];
    const variantsMatchingChangedType = product.productVariants.filter(variant => {
      if (!variant.variantValue) return false;
      const variantValue = variant.variantValue[changedTypeIndex];
      return variantValue && variantValue.variantValueId === justChangedValue;
    });
    
    // If we found variants that match the changed type, pick the first in-stock one or any if none in stock
    if (variantsMatchingChangedType.length > 0) {
      const inStockVariant = variantsMatchingChangedType.find(v => v.stock > 0);
      const bestMatch = inStockVariant || variantsMatchingChangedType[0];
      
      // Update all selected values to match this variant
      if (bestMatch.variantValue) {
        const newValues = {};
        bestMatch.variantValue.forEach((vv, idx) => {
          newValues[`type${idx}`] = vv.variantValueId;
        });
        setSelectedVariantValues(newValues);
      }
      
      handleVariantChange(bestMatch.id);
      return;
    }
    
    // 2. If we can't preserve the changed selection, pick any in-stock variant
    const anyInStockVariant = product.productVariants.find(v => v.stock > 0);
    if (anyInStockVariant) {
      if (anyInStockVariant.variantValue) {
        const newValues = {};
        anyInStockVariant.variantValue.forEach((vv, idx) => {
          newValues[`type${idx}`] = vv.variantValueId;
        });
        setSelectedVariantValues(newValues);
      }
      
      handleVariantChange(anyInStockVariant.id);
      return;
    }
    
    // 3. Last resort: just pick the first variant
    const firstVariant = product.productVariants[0];
    if (firstVariant) {
      if (firstVariant.variantValue) {
        const newValues = {};
        firstVariant.variantValue.forEach((vv, idx) => {
          newValues[`type${idx}`] = vv.variantValueId;
        });
        setSelectedVariantValues(newValues);
      }
      
      handleVariantChange(firstVariant.id);
    }
  };

  const findMatchingVariant = (selectedValues) => {
    if (!product || !product.productVariants) return null;
    
    // Find variant that matches ALL selected values exactly
    return product.productVariants.find(variant => {
      if (!variant.variantValue) return false;
      
      // Check if all selected values match this variant
      return Object.entries(selectedValues).every(([typeKey, valueId]) => {
        const typeIndex = parseInt(typeKey.replace('type', ''));
        return variant.variantValue[typeIndex] && 
               variant.variantValue[typeIndex].variantValueId === valueId;
      });
    });
  };
  
  const handleQuantityChange = (value) => {
    const maxQuantity = !isStockRequired(product)
      ? UNLIMITED_ORDER_QUANTITY
      : (selectedVariant ? selectedVariant.stock : 10);
    const newQuantity = Math.max(1, Math.min(value, maxQuantity));
    setQuantity(newQuantity);
  };
  
  const handleImageChange = (image) => {
    setActiveImage(image);
  };
  
  const handleAddToCart = () => {
    if (!selectedVariant && product.variants && product.variants.length > 0) {
      setCartMessage({
        show: true,
        type: 'error',
        message: 'Veuillez sélectionner une variante'
      });
      return;
    }

    try {
      // Use our CartService to add to cart
      const result = CartService.addToCart(product, quantity, selectedVariant);
      
      if (result.success) {
        setCartMessage({
          show: true,
          type: 'success',
          message: 'Produit ajouté au panier'
        });
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setCartMessage({ show: false, type: '', message: '' });
        }, 3000);
      }
    } catch (err) {
      setCartMessage({
        show: true,
        type: 'error',
        message: 'Erreur lors de l\'ajout au panier'
      });
      console.error(err);
    }
  };
  
  const closeCartMessage = () => {
    setCartMessage(prev => ({ ...prev, show: false }));
  };
  
  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId, loadProduct]);
  
  return {
    product,
    loading,
    error,
    selectedVariant,
    quantity,
    activeImage,
    cartMessage,
    variantGroups,
    variantTypes,
    selectedVariantValues,
    handleVariantChange,
    handleVariantValueChange,
    handleQuantityChange,
    handleImageChange,
    handleAddToCart,
    closeCartMessage
  };
};